import { FC, ProviderProps, useContext, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { DictionaryContext, DictionaryContextType } from './context';

export type IProps = ProviderProps<DictionaryContextType>;

/**
 * Use to provide context to dictionary consumers within the component tree.
 * Can be nested multiple times and lookups will traverse up the tree until a
 * result is found.
 *
 * @example
 *
 * <Dictionary value={{ firstName: 'Jamie', lastName: 'Hoeks' }}>
 *   <Dictionary value={{ planCost: 70 }}>
 *     <App />
 *   </Dictionary>
 * </Dictionary>
 *
 * // Within App component
 * const applyDictionaryToString = useApplyDictionaryToString();
 * const input = 'Hello {{firstName}}, your new service will cost $${{planCost}} per month.';
 * const output = applyDictionaryToString(input);
 * <span>{output}</span>; // => Hello Jamie, your new service will cost $70 per month.
 */
export const Dictionary: FC<IProps> = ({ value, children }) => {
  const parent = useContext(DictionaryContext);
  const merged = { ...parent, ...value };

  // Deep compare so in-place mutations of `value` are picked up, while keeping a
  // stable reference (and avoiding consumer rerenders) when contents are unchanged.
  const providerValueRef = useRef(merged);
  if (!isEqual(merged, providerValueRef.current)) {
    providerValueRef.current = merged;
  }

  return <DictionaryContext.Provider value={providerValueRef.current}>{children}</DictionaryContext.Provider>;
};

export default Dictionary;
